import React, { useState, useEffect } from "react";
import ProductCard from "../../components/common/ProductCardComponent/Index";
import Header from "../../assests/Sale_Header.jpg";
import FilterSidebar from "../../components/FilterSidebar/index";
import ReactPaginate from "react-paginate";
import { fetchSaleProducts, checkSalePageStatus } from "../../Services/ProductServices";
import Spinner from "../../components/common/loadingSpinnercomponent/Spinner";
import { useNavigate } from 'react-router-dom';
import "./Sales.css";

const SalesPage = () => {
  const navigate = useNavigate();
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortOption, setSortOption] = useState("latest");
  const [isPageEnabled, setIsPageEnabled] = useState(true);
  const itemsPerPage = 12;

  const [filters, setFilters] = useState({
    category: null,
    size: null,
    availability: null,
    minPrice: null,
    maxPrice: null,
  });

  // Initialize filters and check sale status
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const initialFilters = {
      category: urlParams.get("category") || null,
      size: urlParams.get("size") || null,
      availability: urlParams.get("availability") || null,
      minPrice: urlParams.get("min_price")
        ? parseInt(urlParams.get("min_price"), 10)
        : null,
      maxPrice: urlParams.get("max_price")
        ? parseInt(urlParams.get("max_price"), 10)
        : null,
    };
    const initialSort = urlParams.get("sort") || "latest";
    setFilters(initialFilters);
    setSortOption(initialSort);

    checkSaleStatus();
  }, []);

  const checkSaleStatus = async () => {
    try {
      const status = await checkSalePageStatus();
      setIsPageEnabled(status.isEnabled);
      
      if (!status.isEnabled) {
        setError("Sale page is currently unavailable. Please check back later.");
        setIsLoading(false);
        return;
      }

      fetchDataWithFilters();
    } catch (error) {
      console.error("Error checking sale status:", error);
      setError("Unable to load sale page. Please try again later.");
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(0); // Reset to first page when filters change
  };

  const buildQueryParams = (filters) => {
    const params = new URLSearchParams();
    params.set("category", filters.category || "");
    params.set("size", filters.size || "");
    params.set("availability", filters.availability || "");
    params.set("min_price", filters.minPrice !== null ? filters.minPrice : "");
    params.set("max_price", filters.maxPrice !== null ? filters.maxPrice : "");
    return params.toString();
  };

  const updateUrlWithQueryString = (queryString, sortOption) => {
    const params = new URLSearchParams(queryString);
    params.set("sort", sortOption);
    const url = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({ path: url }, "", url);
  };

  const fetchDataWithFilters = async () => {
    if (!isPageEnabled) return;

    setIsLoading(true);
    setError(null);
    try {
      const queryString = buildQueryParams(filters);
      updateUrlWithQueryString(queryString, sortOption);
      const productsData = await fetchSaleProducts(queryString);
      setAllProducts(productsData || []);
    } catch (error) {
      console.error("Error fetching products:", error);
      if (error.message === 'Sale page is currently disabled') {
        setError("Sale page is currently unavailable. Please check back later.");
        setIsPageEnabled(false);
      } else {
        setError("Failed to fetch products. Please try again later.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isPageEnabled) {
      fetchDataWithFilters();
    }
  }, [filters, isPageEnabled]);

  const getDisplayPrice = (product) => {
    return product.OfferPrice > 0 ? parseFloat(product.OfferPrice) : parseFloat(product.Price);
  };

  useEffect(() => {
    if (!isLoading && !error && allProducts.length > 0) {
      let sortedProducts = [...allProducts];

      switch (sortOption) {
        case "price_low":
          sortedProducts.sort((a, b) => getDisplayPrice(a) - getDisplayPrice(b));
          break;
        case "price_high":
          sortedProducts.sort((a, b) => getDisplayPrice(b) - getDisplayPrice(a));
          break;
        case "latest":
        default:
          sortedProducts.sort(
            (a, b) => new Date(b.DateCreated) - new Date(a.DateCreated)
          );
          break;
      }

      setFilteredProducts(sortedProducts);
    } else {
      setFilteredProducts([]);
    }
  }, [sortOption, allProducts, isLoading, error]);

  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const updatedFilters = {
        category: urlParams.get("category") || null,
        size: urlParams.get("size") || null,
        availability: urlParams.get("availability") || null,
        minPrice: urlParams.get("min_price")
          ? parseInt(urlParams.get("min_price"), 10)
          : null,
        maxPrice: urlParams.get("max_price")
          ? parseInt(urlParams.get("max_price"), 10)
          : null,
      };
      const updatedSort = urlParams.get("sort") || "latest";
      setFilters(updatedFilters);
      setSortOption(updatedSort);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getPagedData = () => {
    if (!filteredProducts || filteredProducts.length === 0) return [];
    const offset = currentPage * itemsPerPage;
    return filteredProducts.slice(offset, offset + itemsPerPage);
  };

  const calculateDiscount = (regularPrice, offerPrice) => {
    const regular = parseFloat(regularPrice);
    const offer = parseFloat(offerPrice);
    return Math.round(((regular - offer) / regular) * 100);
  };

  const currentItems = getPagedData();
  const endItem = Math.min((currentPage + 1) * itemsPerPage, filteredProducts.length || 0);

  if (isLoading) {
    return (
      <div className="loading-container">
        <Spinner />
      </div>
    );
  }

  if (!isPageEnabled) {
    return (
      <div className="sale-page-disabled">
        <h1>Sale Page Unavailable</h1>
        <p>No products available on sale at the moment. Please check back later!</p>
        <button className="return-home-btn" onClick={() => navigate('/')}>
          Return to Home
        </button>
      </div>
    );
  }

  if (error) {
    return <div className="error-container">Error: {error}</div>;
  }

  return (
    <div>
      <header className="header-image-container">
        <img src={Header} alt="Header" className="img-fluid" />
      </header>
      <div className="row mt-4 mx-0">
        <h1 className="h4 heading">Sale Products</h1>
        <div className="col text-right">
          <span className="items-count">
            Showing {endItem} of {filteredProducts?.length || 0} results
          </span>
          <div className="product-dropdown-container">
            <select
              className="custom-dropdown"
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
            >
              <option value="latest">Sort By Latest</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
            </select>
            <i className="fa-solid fa-chevron-down dropdown-icon"></i>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-3">
            <FilterSidebar
              onFilterChange={handleFilterChange}
              initialFilters={filters}
            />
          </div>
          <div className="col-lg-9">
            {currentItems.length === 0 ? (
              <div className="no-products">
                <h2>No Products Currently on Sale</h2>
                <p>Check back later for amazing deals!</p>
              </div>
            ) : (
              <div className="product-list-grid">
                {currentItems.map((product) => (
                  <div key={product.ProductId} className="product-card-wrapper">
                    <span className="discount-chip">
                      -{calculateDiscount(product.Price, product.OfferPrice)}%
                    </span>
                    <ProductCard product={product} />
                  </div>
                ))}
              </div>
            )}
            {filteredProducts.length > itemsPerPage && (
              <div className="pagination-container">
                <ReactPaginate
                  previousLabel={<i className="fa-solid fa-chevron-left"></i>}
                  nextLabel={<i className="fa-solid fa-chevron-right"></i>}
                  breakLabel="..."
                  pageCount={Math.ceil(filteredProducts.length / itemsPerPage)}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={5}
                  onPageChange={handlePageClick}
                  containerClassName="pagination"
                  activeClassName="active"
                  pageClassName="page-item"
                  pageLinkClassName="page-link"
                  previousClassName="page-item"
                  previousLinkClassName="page-link"
                  nextClassName="page-item"
                  nextLinkClassName="page-link"
                  breakClassName="page-item"
                  breakLinkClassName="page-link"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesPage;